/**
 * @file useShopDashboard.ts
 * @description Данные «Сводки» магазина (phase19.31, разбор A макет (г)).
 *
 * Блоки независимы: сбой одного запроса не гасит остальные (`allSettled`).
 *  - показатели и топ товаров — за выбранный период (`?days=` в адресе:
 *    ссылку на «Сводку за 7 дней» можно переслать, F5 не сбрасывает);
 *  - последние заказы — те же строки, что реестр (`GET /admin/orders`);
 *  - «Требует внимания» — `GET /admin/nav-counters` (те же числа, что у
 *    меню) и число заказов «Мой ход» (`owner=me`).
 * Смена периода перечитывает только показатели и топ.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { parseDashboardPeriod, type DashboardPeriod } from "./dashboard-model";
import { getNavCounters } from "../../lib/api-nav-counters";
import { getOrderStats, getTopSkus, listOrders, type OrderDto, type OrderStatsDto, type TopSkuDto } from "../../lib/api-orders";
import type { NavCounters } from "../../lib/shop-nav";

/** Сколько последних заказов на сводке. */
const RECENT_LIMIT = 5;
/** Сколько товаров в топе. */
const TOP_LIMIT = 10;

export interface ShopDashboardState {
  period: DashboardPeriod;
  setPeriod: (days: DashboardPeriod) => void;
  stats: OrderStatsDto | null;
  /** За какой период пришли показатели (пока идёт новый запрос — прежний). */
  statsPeriod: DashboardPeriod;
  statsError: boolean;
  top: TopSkuDto[] | null;
  topError: boolean;
  recent: OrderDto[] | null;
  recentError: boolean;
  counters: NavCounters;
  myTurn: number | null;
  /** Счётчики «Требует внимания» ещё не пришли. */
  attentionLoading: boolean;
  /** Первая загрузка показателей (заготовки плиток). */
  loading: boolean;
  reload: () => void;
}

export function useShopDashboard(canViewOrders: boolean): ShopDashboardState {
  const [params, setParams] = useSearchParams();
  const period = parseDashboardPeriod(params.get("days"));
  const [stats, setStats] = useState<OrderStatsDto | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<DashboardPeriod>(period);
  const [statsError, setStatsError] = useState(false);
  const [top, setTop] = useState<TopSkuDto[] | null>(null);
  const [topError, setTopError] = useState(false);
  const [recent, setRecent] = useState<OrderDto[] | null>(null);
  const [recentError, setRecentError] = useState(false);
  const [counters, setCounters] = useState<NavCounters>({});
  const [myTurn, setMyTurn] = useState<number | null>(null);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [loading, setLoading] = useState(canViewOrders);
  const [tick, setTick] = useState(0);

  // Показатели и топ — за период.
  useEffect(() => {
    if (!canViewOrders) return;
    let alive = true;
    setLoading(true);
    void Promise.allSettled([getOrderStats(period), getTopSkus(TOP_LIMIT, period)]).then(([s, t]) => {
      if (!alive) return;
      setStats(s.status === "fulfilled" ? s.value : null);
      setStatsPeriod(period);
      setStatsError(s.status === "rejected");
      setTop(t.status === "fulfilled" ? t.value : null);
      setTopError(t.status === "rejected");
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [canViewOrders, period, tick]);

  // Последние заказы, счётчики меню и «Мой ход» — от периода не зависят.
  useEffect(() => {
    let alive = true;
    const orders = canViewOrders
      ? Promise.allSettled([listOrders({ limit: RECENT_LIMIT }), listOrders({ owner: "me", limit: 1 })])
      : Promise.resolve(null);
    void Promise.allSettled([getNavCounters(), orders]).then(([c, o]) => {
      if (!alive) return;
      setCounters(c.status === "fulfilled" ? (c.value ?? {}) : {});
      setAttentionLoading(false);
      const pair = o.status === "fulfilled" ? o.value : null;
      if (!pair) return;
      const [list, mine] = pair;
      setRecent(list.status === "fulfilled" ? list.value.items : null);
      setRecentError(list.status === "rejected");
      setMyTurn(mine.status === "fulfilled" ? mine.value.total : null);
    });
    return () => {
      alive = false;
    };
  }, [canViewOrders, tick]);

  const setPeriod = useCallback(
    (days: DashboardPeriod) => {
      const next = new URLSearchParams(params);
      next.set("days", String(days));
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const reload = useCallback(() => setTick((n) => n + 1), []);

  return { period, setPeriod, stats, statsPeriod, statsError, top, topError, recent, recentError, counters, myTurn, attentionLoading, loading, reload };
}
